import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { CalendarGrid } from '../components/CalendarGrid'
import { FilterChipsRow } from '../components/FilterChipsRow'
import { MonthNavHeader } from '../components/MonthNavHeader'
import { EventRow } from '../components/EventRow'
import { api, type ApiEvent } from '../services/api'
import { getMonthViewInfo, isEventOnDay, getCalendarDays } from '../utils/monthView'
import { getEventListParams } from '../utils/eventFilterParams'
import styles from './CalendarScreen.module.css'

export function CalendarScreen() {
  const navigate = useNavigate()
  const [events, setEvents] = useState<ApiEvent[]>([])
  const [activeFilter, setActiveFilter] = useState('All')
  const [selectedDay, setSelectedDay] = useState<number | null>(null)

  useEffect(() => {
    api.events.list(getEventListParams(activeFilter))
      .then(setEvents)
      .catch(() => setEvents([]));
  }, [activeFilter]);

  const [viewDate, setViewDate] = useState(new Date());
  const { year, month, monthLabel, firstDayOfMonth, daysInMonth } = getMonthViewInfo(viewDate);

  useEffect(() => {
    setSelectedDay(null);
  }, [year, month]);

  const monthEvents = events
    .filter(e => {
      const d = new Date(e.event_date);
      return d.getFullYear() === year && d.getMonth() === month;
    })
    .sort((a, b) => a.event_date.localeCompare(b.event_date));

  const listEvents = selectedDay
    ? monthEvents.filter(e => isEventOnDay(e.event_date, selectedDay, month, year))
    : monthEvents;

  return (
    <div className={styles.container}>
      <div className={styles.stickyTop}>
        <FilterChipsRow activeFilter={activeFilter} onSelect={setActiveFilter} className={styles.filterChips} />
        <MonthNavHeader
          monthLabel={monthLabel}
          onPrev={() => setViewDate(new Date(year, month - 1, 1))}
          onNext={() => setViewDate(new Date(year, month + 1, 1))}
          className={styles.header}
          navBtnClassName={styles.navBtn}
        />
        <CalendarGrid
          days={getCalendarDays(firstDayOfMonth, daysInMonth)}
          hasEvent={(day) => day > 0 && events.some(e => isEventOnDay(e.event_date, day, month, year))}
          onClick={(day) => setSelectedDay(prev => (day > 0 && prev !== day ? day : null))}
        />
      </div>
      <div className={styles.agendaHeader}>
        <span>{selectedDay ? `${monthLabel.split(' ')[0]} ${selectedDay}` : `All of ${monthLabel}`}</span>
        {selectedDay && (
          <button className={styles.clearDayBtn} onClick={() => setSelectedDay(null)}>Show full month</button>
        )}
      </div>
      {listEvents.length === 0 ? (
        <div className={styles.eventItemNoEvents}>No events.</div>
      ) : (
        <div className={styles.eventList}>
          {listEvents.map(ev => <EventRow key={ev.id} event={ev} onClick={() => navigate(`/event/${ev.id}`)} />)}
        </div>
      )}
    </div>
  );
}
